import React, { useRef, useState } from 'react';
import { FaUserCircle } from "react-icons/fa";
import './Profile.css';

interface ProfileProps {
  onImageSelect: (file: File) => void;
  currentImage?: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const Profile: React.FC<ProfileProps> = ({
  onImageSelect,
  currentImage,
  size = 'medium',
  className = ''
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleContainerClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      onImageSelect(file);
      
      // Reset input to allow selecting same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small': return 32;
      case 'large': return 80;
      default: return 48;
    }
  };

  const getContainerSize = () => {
    switch (size) {
      case 'small': return '60px';
      case 'large': return '120px';
      default: return '80px';
    }
  };

  return (
    <div className={`profile-image-selector ${className}`}>
      <div
        className={`profile-image-selector__container profile-image-selector__container--${size}`}
        onClick={handleContainerClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ width: getContainerSize(), height: getContainerSize() }}
      >
        {currentImage ? (
          <img
            src={currentImage}
            alt="Profile"
            className="profile-image-selector__image"
          />
        ) : (
          <FaUserCircle 
            className="profile-image-selector__icon" 
            size={getIconSize()}
          />
        )}
        
        {/* Hover overlay */}
        <div className={`profile-image-selector__overlay ${isHovered ? 'profile-image-selector__overlay--visible' : ''}`}>
          <div className="profile-image-selector__overlay-content">
            <span className="profile-image-selector__camera-icon">📷</span>
            <span className="profile-image-selector__text">
              {currentImage ? 'Change' : 'Add'} Photo
            </span>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="profile-image-selector__file-input"
        />
      </div>
    </div>
  );
};

export default Profile;